import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  inject,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  NotificationRef,
  NotificationService,
} from '@progress/kendo-angular-notification';
import { EMPTY, take, takeUntil } from 'rxjs';
import {
  ActionsRealtimeService,
  ActionsStore,
} from '@maximizer/actions/data-access';
import {
  ACTIONS_MODULE_ID,
  RecipeRunCompleted,
} from '@maximizer/actions/domain';
import { ContextService } from '@maximizer/core/shared/data-access';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import {
  ActionEventType,
  ChangeCurrentModule,
} from '@maximizer/core/shared/ui/config';
import { ActionsToastComponent } from './actions-toast.component';

const NOTHING_TO_REVIEW_MS = 5000;

@Component({
  selector: 'maximizer-ai-actions-host',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export class ActionsHostComponent extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  private readonly realtime = inject(ActionsRealtimeService);
  private readonly store = inject(ActionsStore);
  private readonly context = inject(ContextService);
  private readonly translate = inject(TranslateService);
  private readonly notifications = inject(NotificationService);
  private readonly ngZone = inject(NgZone);

  private initialized = false;

  constructor() {
    super(inject(ElementRef));
  }

  override doAction(name: string, args?: unknown): void {
    this.ngZone.run(() => {
      if (name === ActionEventType.Initialize) {
        this.initialize();
      } else {
        super.doAction(name, args);
      }
    });
  }

  private initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.realtime.connect();

    this.store.load();

    this.addSubscription(
      this.realtime.runCompleted$.subscribe((run) => this.notify(run)),
    );
  }

  private notify(run: RecipeRunCompleted): void {
    const needsReview = run.actionCount > 0;

    const ref = this.notifications.show({
      content: ActionsToastComponent,
      closable: needsReview,
      hideAfter: needsReview ? 0 : NOTHING_TO_REVIEW_MS,
      type: { style: 'none', icon: false },
      cssClass: 'ai-actions-toast',
      position: { horizontal: 'right', vertical: 'bottom' },
      animation: { type: 'slide', duration: 500 },
    });

    const content = ref.content;
    if (!content) {
      return;
    }

    const toast = content.instance as ActionsToastComponent;
    toast.message = this.messageFor(run);
    toast.showReview = needsReview;
    content.changeDetectorRef.detectChanges();

    outputToObservable(toast.review)
      .pipe(takeUntil(ref.afterHide ?? EMPTY), take(1))
      .subscribe(() => this.review(ref));
  }

  private messageFor(run: RecipeRunCompleted): string {
    const recipe = this.translate.instant(
      run.clientName
        ? 'actionsInbox.toast.recipeWithClient'
        : 'actionsInbox.toast.recipeNoClient',
      { recipe: run.recipeName, client: run.clientName },
    );

    return `${recipe} ${this.outcomeFor(run.actionCount)}`;
  }

  private outcomeFor(count: number): string {
    if (count === 0) {
      return this.translate.instant('actionsInbox.toast.nothingToReview');
    }

    if (count === 1) {
      return this.translate.instant('actionsInbox.toast.needsReviewOne');
    }

    return this.translate.instant('actionsInbox.toast.needsReviewMany', {
      count,
    });
  }

  private review(ref: NotificationRef): void {
    ref.hide();

    if (this.context.currentModuleId === ACTIONS_MODULE_ID) {
      return;
    }

    this.actionCompleted.emit(new ChangeCurrentModule(ACTIONS_MODULE_ID));
  }
}
